import axios from 'axios';
import { getApiBaseUrl } from '../utils/urlHandler';
import { debug } from '../utils/debugLogger';
import { AnalyticsSummary, PostAnalytics } from '../types/analytics';

const BASE_URL = getApiBaseUrl('REACT_APP_CONTENT_API_URL');
interface AnalyticsQueryOptions {
  platform?: string;
  startDate?: string;
  endDate?: string;
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const normalizeStartDateParam = (value?: string): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (DATE_ONLY_PATTERN.test(trimmed)) return `${trimmed}T00:00:00Z`;
  return trimmed;
};

const normalizeEndDateParam = (value?: string): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (DATE_ONLY_PATTERN.test(trimmed)) return `${trimmed}T23:59:59Z`;
  return trimmed;
};

const buildAnalyticsQueryParams = (options?: AnalyticsQueryOptions) => {
  if (!options) return undefined;
  const params: Record<string, string> = {};
  if (options.platform) params.platform = options.platform;
  const normalizedStartDate = normalizeStartDateParam(options.startDate);
  const normalizedEndDate = normalizeEndDateParam(options.endDate);
  if (normalizedStartDate) params.start_date = normalizedStartDate;
  if (normalizedEndDate) params.end_date = normalizedEndDate;
  return Object.keys(params).length ? params : undefined;
};

export const fetchAnalyticsSummary = async (
  customerId: string,
  idToken: string,
  options?: AnalyticsQueryOptions
): Promise<AnalyticsSummary> => {
  const endpoint = `${BASE_URL}/${customerId}/analytics/summary`;
  try {
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      params: buildAnalyticsQueryParams(options),
    });
    return response.data;
  } catch (error: any) {
    debug.error('Error fetching analytics summary:', error);
    throw new Error(
      error.response?.data?.message || 'Failed to fetch analytics summary'
    );
  }
};

export const fetchPostAnalyticsList = async (
  customerId: string,
  idToken: string,
  options?: AnalyticsQueryOptions
): Promise<PostAnalytics[]> => {
  const endpoint = `${BASE_URL}/${customerId}/analytics/posts`;
  try {
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      params: buildAnalyticsQueryParams(options),
    });
    if (response.data && Array.isArray(response.data.items)) {
      return response.data.items;
    }
    return response.data.analytics || (Array.isArray(response.data) ? response.data : []);
  } catch (error: any) {
    debug.error('Error fetching post analytics list:', error);
    throw new Error(
      error.response?.data?.message || 'Failed to fetch post analytics list'
    );
  }
};

export const fetchSinglePostAnalytics = async (
  customerId: string,
  postId: string,
  idToken: string
): Promise<PostAnalytics> => {
  const endpoint = `${BASE_URL}/${customerId}/analytics/posts/${postId}`;
  try {
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    debug.error(`Error fetching analytics for post ${postId}:`, error);
    throw new Error(
      error.response?.data?.message || 'Failed to fetch post analytics'
    );
  }
};

// ── On-demand analytics collection trigger ──────────────────────────────────
// Endpoint lives on social-management API (same as publish post)
const SOCIAL_API_BASE = getApiBaseUrl('REACT_APP_SOCIAL_API_URL');

export interface TriggerCollectionOptions {
  platform: string;
}

export interface TriggerCollectionResponse {
  message: string;
  customer_id: string;
  lookback_days?: number;
  platforms_triggered: string[];
}

/**
 * Trigger on-demand analytics collection for a single platform.
 * platform is required — exactly one collector Lambda is invoked asynchronously.
 * No date range is sent; collectors always scan ALL published posts for the
 * given customer + platform from PostsV2.
 * Returns immediately; poll fetchAnalyticsSummary.last_collected_at to detect completion.
 */
export const triggerAnalyticsCollection = async (
  customerId: string,
  idToken: string,
  options: TriggerCollectionOptions
): Promise<TriggerCollectionResponse> => {
  const endpoint = `${SOCIAL_API_BASE}/${customerId}/analytics/collect`;
  try {
    const response = await axios.post(
      endpoint,
      {
        platform: options.platform,
      },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    debug.log('Analytics collection triggered:', response.data);
    return response.data;
  } catch (error: any) {
    debug.error('Error triggering analytics collection:', error);
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to trigger analytics collection'
    );
  }
};
